import React, { useEffect, useState } from 'react';

import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { AuthUserLoginRequest } from '../../models/AuthUserLoginRequest';
import { useNavigate } from 'react-router-dom';
import { AuthAPIService } from '../../services/AuthAPIServices';
import { BaseControlProps } from '../../models/BaseControlProps';
import { ReactComponent as SvgLogo } from "../../components/Header/logo.svg";
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

import './Login.css';
import { Box, Button, CircularProgress, FormControl, FormLabel, Link, TextField, Typography } from '@mui/material';
import { AuthUserValidateMFARequest } from '../../models/AuthUserValidateMFARequest';
import { AuthUserResendMFARequest } from '../../models/AuthUserResendMFARequest';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '450px',
    },
    boxShadow:
      'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
      boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
  }));

const SignInContainer = styled(Stack)(({ theme }) => ({
    padding: 20,
    marginTop: '10vh',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0
    },
  }));

const Login = (props: BaseControlProps) => {
    const [scope, apply] = React.useState<AuthUserLoginRequest>({userName: "", password: "", ipAddress: "", ssoTokenString: ""});
    const [scopeMFA, applyMFA] = useState<AuthUserValidateMFARequest>({
        hashedUser: "",
        mfaCode: "",
    });
    const [scopeResendMFA, applyResendMFA] = useState<AuthUserResendMFARequest>({
        hashedUser: "",
    });
    const [disabled, setDisabled] = useState<boolean>(false);
    const [disabledMFA, setDisabledMFA] = useState<boolean>(false);
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [inputValid, isInputValid] = useState<boolean>(false);
    const [requiresMFA, setRequiresMFA] = useState<boolean>(false);
    const [hashedUser, setHashedUser] = useState<string>('');
    const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [timer, setTimer] = useState(0);
    const [timerRunning, isTimerRunning] = useState(false);
    const [expireTimer, setExpireTimer] = useState(0);
    const [expireTimerRunning, setExpireTimerRunning] = useState(false);
    const loginContext = useAuth();
    const navigate = useNavigate();
    const authService = new AuthAPIService();

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }


    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!inputValid) {return;}

        setDisabled(true);
        authService.login(scope).then((response) => {
            if(response.error){
                if (props.isMessageInfoShow && props.setMessageInfo) {
                    props.setMessageInfo(UtilService.getErrorMessageInfo(response.error));
                    props.isMessageInfoShow(true);
                }
            }
            else {
                if(response.response?.hashedUser && response.response.requiresMFA){
                    startTimer();
                    startExpireTimer();
                    setRequiresMFA(response.response.requiresMFA);
                    setHashedUser(response.response.hashedUser);
                }
                else{
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getErrorMessageInfo("Invalid User Name or Password"));
                        props.isMessageInfoShow(true);
                    }
                }
            }
            setDisabled(false);
        })
    }

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;
        isInputValid(true);

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
          isInputValid(false);
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
    
        if (!password.value || password.value.length < 6) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must be at least 6 characters long.');
          isInputValid(false);
        } else {
          setPasswordError(false);
          setPasswordErrorMessage('');
        }
    
        return inputValid;
      };
    
    const handleChange = (value: string, index: number) => {
        if (value.match(/^[0-9]$/) || value === "") {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Focus the next input field
            if (value !== "" && index < otp.length - 1) {
            const nextField = document.getElementById(`otp-${index + 1}`);
            nextField?.focus();
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === "Backspace" && otp[index] === "" && index > 0) {
            const prevField = document.getElementById(`otp-${index - 1}`);
            prevField?.focus();
        }
    };

    const validateMFA = async (event: any) => {
        event.preventDefault();

        scopeMFA.hashedUser = hashedUser;
        scopeMFA.mfaCode = otp.join("");
        applyMFA(scopeMFA);
        setDisabledMFA(true);
        authService.validateMFA(scopeMFA).then((response) => {
            setDisabledMFA(false);
            if(response.error){
                if (props.isMessageInfoShow && props.setMessageInfo) {
                    setOtp(["", "", "", "", "", ""]);
                    props.setMessageInfo(UtilService.getErrorMessageInfo(response.error));
                    props.isMessageInfoShow(true);
                }
            }
            else {
                if(response.response?.token){
                    loginContext.login(response.response);

                    navigate("/home");
                }
                else{
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        setOtp(["", "", "", "", "", ""]);
                        props.setMessageInfo(UtilService.getErrorMessageInfo("Invalid User Name or Password"));
                        props.isMessageInfoShow(true);
                    }
                }
            }
        })
    }

    const handleResendMFA = async () => {
        setDisabledMFA(true);
        scopeResendMFA.hashedUser = hashedUser;
        applyResendMFA(scopeResendMFA);
        authService.resendMFA(scopeResendMFA).then((response) => {
            setDisabledMFA(false);
            if (response.error) {
                if (props.isMessageInfoShow && props.setMessageInfo) {
                    props.setMessageInfo(UtilService.getErrorMessageInfo(response.error));
                    props.isMessageInfoShow(true);
                }
            }
            else {
                if(response.response?.isSuccess){
                    startTimer();
                    startExpireTimer();
                    setHashedUser(response.response.hashedUser);
                }
                else{
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getErrorMessageInfo("Error re-sending the MFA code"));
                        props.isMessageInfoShow(true);
                    }
                }
            }
        })
    }

    const startTimer = () => {
        setTimer(180);
        isTimerRunning(true);
        setResendDisabled(true);
    }

    const startExpireTimer = () => {
        setExpireTimer(600);
        setExpireTimerRunning(true);
    }

    useEffect(() => {
        if (timerRunning && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            isTimerRunning(false);
            setResendDisabled(false);
        }
    }, [timer, timerRunning]);

    useEffect(() => {
        if (expireTimerRunning && expireTimer > 0) {
            const interval = setInterval(() => {
                setExpireTimer((prevTimer) => prevTimer - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setRequiresMFA(false);
            setHashedUser("");
        }

    }, [expireTimer, expireTimerRunning])

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    }

    return (
        <SignInContainer direction="column" justifyContent="space-between">
            <Card variant="outlined">
                <div className="logo-container">
                    <SvgLogo focusable={false} viewBox="0 0 500 150" />
                </div>
                <Typography 
                    component="h1" 
                    variant="h6" 
                >
                    Login To Your Account
                </Typography>

                {!requiresMFA && (
                    <Box
                        component="form"
                        onSubmit={handleLogin}
                        noValidate
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            gap: 2,   
                        }}
                    >
                        <FormControl>
                            <FormLabel htmlFor='email'>Email:</FormLabel>
                            <TextField
                                error={emailError}
                                helperText={emailErrorMessage}
                                id="email"
                                type="email"
                                name="email"
                                placeholder="your@email.com"
                                autoComplete="email"
                                autoFocus
                                required
                                fullWidth
                                variant="outlined"
                                color={emailError ? 'error' : 'primary'}
                                sx={{ ariaLabel: 'email' }}
                                onChange={(e)=>{
                                    scope.userName = e.currentTarget.value;
                                    applyScope();
                                }}
                            />

                        </FormControl>
                        <FormControl>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <FormLabel htmlFor="password">Password</FormLabel>
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => { navigate("/forget-password"); }}
                                    variant="body2"
                                    sx={{ alignSelf: 'baseline' }}
                                >
                                    <b>Forgotten your Password?</b>
                                </Link>
                            </Box>
                            <TextField 
                                error={passwordError}
                                helperText={passwordErrorMessage}
                                name="password"
                                placeholder="••••••••"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                autoFocus
                                required
                                fullWidth
                                variant="outlined"
                                color={passwordError ? 'error' : 'primary' }
                                onChange={(e)=>{
                                    scope.password = e.currentTarget.value;
                                    applyScope();
                                }}
                            />
                        </FormControl>
                        <Button 
                            type="submit"
                            fullWidth
                            variant="contained"
                            onClick={validateInputs}
                            disabled={disabled}
                            startIcon={disabled ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                '&.Mui-disabled': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                }
                            }}
                        >
                            {disabled ? 'Loading...' : 'Sign in'}
                        </Button>
                        <Typography sx={{ textAlign: 'center' }}>
                            Is this your first time? Please <Link component="button" variant="body2" sx={{ alignSelf: 'center' }} onClick={() => { navigate("/validate"); }} ><b>Register</b></Link>
                        </Typography>
                    </Box>
                )}

                {requiresMFA && (
                    <Box
                        component="form"
                        onSubmit={validateMFA}
                        noValidate
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            marginTop: 2,
                            width: "100%",
                        }}
                    >
                        <Typography component="h2" variant="subtitle2">
                            Enter Verification Code
                        </Typography>
                        <Typography id="otp-instructions" variant="body1">
                            Check your email for a verification code. The code will expire in <b>10</b> minutes.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                            }}
                        >
                            {otp.map((value, index) => (
                                <TextField
                                key={index}
                                id={`otp-${index}`}
                                value={value}
                                onChange={(e) => handleChange(e.target.value, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                variant="outlined"
                                inputProps={{
                                    'aria-label': `Digit ${index + 1} of 6`,
                                    'aria-describedby': 'otp-instructions',
                                    maxLength: 1,
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    style: {
                                        textAlign: "center",
                                        fontSize: "20px",
                                        width: "34px",
                                        height: "34px",
                                    },
                                }}
                                />
                            ))}
                        </Box>
                        <Button
                            type='submit'
                            fullWidth
                            variant="contained"
                            onClick={validateMFA}
                            disabled={disabledMFA}
                            startIcon={disabledMFA ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                '&.Mui-disabled': {
                                  color: (theme) => theme.palette.primary.contrastText,
                                }
                            }}
                        >
                            {disabledMFA ? 'Loading...' : 'Verify'}
                        </Button>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography variant="body2" fontSize="medium">
                                Didn't receive the code?
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleResendMFA}
                                disabled={resendDisabled || disabledMFA}
                            >
                                {disabledMFA ? 'Loading...' : 'Resend'}  {resendDisabled && `in (${formatTime(timer)}) minutes`}
                            </Button>
                        </Box>
                    </Box>
                )}

            </Card>
        </SignInContainer>
    );
}

export default Login;